import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listTickets } from '../api/client';
import type { TicketStatus } from '../types';
import { timeAgo } from '../utils/format';

const STATUS_OPTIONS: TicketStatus[] = ['open', 'in_progress', 'resolved', 'closed'];

const STATUS_LABEL: Record<TicketStatus, string> = {
  open:        '🟢 Open',
  in_progress: '🔵 In progress',
  resolved:    '✅ Resolved',
  closed:      '⚪ Closed',
};

export function TicketsPage() {
  const [status, setStatus] = useState<TicketStatus>('open');

  const { data, isLoading, error } = useQuery({
    queryKey: ['tickets', status],
    queryFn: () => listTickets(status, 50),
  });

  const items = data?.items || [];

  return (
    <div data-testid="tickets-page">
      <h1 className="text-xl font-semibold text-gray-900">Support tickets</h1>
      <p className="mt-1 text-sm text-gray-500">
        Tickets opened from <code>/setup → ❓ Help &amp; Support</code>. Replying here delivers
        the response as a Telegram DM to the user; if the DM bounces, the user can still read
        it via the same menu.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="tickets-filters">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as TicketStatus)}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm"
          data-testid="filter-status"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <span className="text-sm text-gray-400">
          {data ? `${items.length} ticket${items.length === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      {isLoading && (
        <div className="mt-6 rounded-md border border-gray-200 bg-white p-6 text-sm text-gray-500">
          Loading tickets…
        </div>
      )}
      {error && (
        <div className="mt-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {(error as Error).message}
        </div>
      )}

      {!isLoading && !error && items.length === 0 && (
        <div className="mt-6 rounded-md border border-gray-200 bg-white p-6 text-sm text-gray-500" data-testid="tickets-empty">
          No tickets in this status.
        </div>
      )}

      {!isLoading && !error && items.length > 0 && (
        <div className="mt-6 overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Ticket</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Project</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">User</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Subject</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Replies</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Opened</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.map((t) => (
                <tr key={t.ticketId} data-testid={`ticket-row-${t.ticketId}`} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-4 py-2 text-sm font-mono text-primary-700">
                    <Link to={`/tickets/${encodeURIComponent(t.ticketId)}`} className="hover:underline">
                      {t.ticketId}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">{t.projectName || <span className="text-gray-400">—</span>}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                    {t.username ? <span>@{t.username}</span> : <span className="font-mono text-xs text-gray-500">{t.userId}</span>}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700 max-w-md truncate">{t.subject}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-500">{t.replies.length}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-500">
                    {t.createdAt ? timeAgo(t.createdAt) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
